import { Button, Grid } from '@material-ui/core';
import { defer } from '@thalesrc/js-utils';
import React, { useCallback, useContext, useRef } from 'react';

import { EmployeeSearchResult, getEmployeesByIds, searchEmployee } from 'api/hs/employee';
import { PpeAssignmentsResult, updatePpeAssignments } from 'api/hs/ppe/ppe-assignments';
import Dialog from 'Common/Dialog';
import AutoComplete from 'Common/Form/AutoComplete';
import { EmployeeSelectionCacheContext } from 'Common/Form/EmployeeSelection/employee-selection.context';
import Form, { FormRef } from 'Common/Form/Form';
import { SelectOption } from 'Common/Form/Select';
import { AlertContext } from 'context/alert.context';
import { SelectOptionsContext } from 'context/select-options.context';
import { useAsyncEffect } from 'utils';

interface Props {
  open: boolean;

  onClose(success: boolean): void;

  data: PpeAssignmentsResult;
}

function mapEmployeeToOptions(types: EmployeeSearchResult[]): SelectOption<string>[] {
  return types.map<SelectOption<string>>(({ id, name }) => ({ text: name, value: id }));
}

async function employeeOptionFetcher(value: string) {
  const res = await searchEmployee(value);

  return mapEmployeeToOptions(res);
}

async function employeeLabelFetcher(values: string[]) {
  const res = await getEmployeesByIds(values);

  return mapEmployeeToOptions(res);
}

export default function AssigmentEmployeeForm({ open, onClose, data }: Props) {
  const { showMessage } = useContext(AlertContext);
  const formRef = useRef<FormRef<Record<string, any>>>();
  const { ppeDefinitions } = useContext(SelectOptionsContext);
  const { loadEmployees } = useContext(EmployeeSelectionCacheContext);

  const handleSubmit = useCallback(
    async values => {
      try {
        const payload = {
          employee: values.employeeIds?.map((employeeId: any) => {
            return {
              assignmentType: 'EMPLOYEE',
              employeeId,
            };
          }),
        };
        await updatePpeAssignments(values.ppeDefinitionId, payload.employee);
        showMessage(' İşlem başarılı.', 'success', 'info');
        onClose(true);
      } catch (err) {
        showMessage('İşlem sırasında sorun oluştu.', 'error', 'info');
        onClose(false);
      }
    },
    [onClose, showMessage]
  );

  useAsyncEffect(async () => {
    if (!data) {
      return;
    }

    await defer();
    await loadEmployees(data?.ppeAssignments?.reduce((acc, item) => [...acc, item?.employeeId], []));
    formRef?.current?.methods?.setValue(
      'employeeIds',
      data?.ppeAssignments?.reduce((acc, item) => [...acc, item?.employeeId], [])
    );
    formRef?.current?.methods?.setValue('ppeDefinitionId', data?.id);

    formRef?.current?.methods?.trigger();
  }, [data, open]);

  return (
    <Dialog width="sm" title="POZISYONA GÖRE KKD ATAMA" color="secondary" opened={open} onClose={onClose.bind(null, false)}>
      <Form onSubmit={handleSubmit} ref={formRef}>
        <Grid container spacing={1}>
          <Grid item xs={12} md={12} lg={12} sm={12}>
            <AutoComplete required single name="ppeDefinitionId" options={ppeDefinitions} label="KKD Tanım" />
          </Grid>
          <Grid item xs={12} md={12} lg={12} sm={12}>
            <AutoComplete
              name="employeeIds"
              label="Personel Seç"
              optionFetcher={employeeOptionFetcher}
              labelFetcher={employeeLabelFetcher}
            />
          </Grid>
          <Grid item xs={12} md={12} lg={12} sm={12} className="text-right">
            <Button variant="contained" type="submit" color="secondary" className="mr-2">
              {data?.id ? 'Güncelle' : 'Kaydet'}
            </Button>
            <Button variant="contained" type="button" onClick={onClose.bind(null, false)}>
              Vazgeç
            </Button>
          </Grid>
        </Grid>
      </Form>
    </Dialog>
  );
}
